import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'

const languages = [
  { code: 'es', name: 'Español', flag: '\u{1F1E6}\u{1F1F7}' }, // AR flag
  { code: 'en', name: 'English', flag: '\u{1F1FA}\u{1F1F8}' }, // US flag
]

export default function LanguageSelector() {
  const { i18n, t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0]

  // Cerrar dropdown al hacer click afuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode)
    setIsOpen(false)
  }

  return (
    <div
      ref={dropdownRef}
      className="fixed bottom-5 right-5 z-[9999]"
    >
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-br from-zinc-800 to-zinc-900 border-2 border-casino-gold rounded-full text-casino-gold font-bold text-sm transition-all duration-300 hover:from-zinc-700 hover:to-zinc-800 hover:-translate-y-0.5 shadow-lg hover:shadow-casino-gold/20"
        title={t('common.language')}
      >
        <span className="text-lg">{currentLang.flag}</span>
        <span className="font-cinzel hidden sm:inline">{currentLang.code.toUpperCase()}</span>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-full right-0 mb-2 bg-zinc-900 border-2 border-casino-gold rounded-xl overflow-hidden shadow-xl"
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`flex items-center gap-3 w-full px-5 py-3 text-sm text-left transition-all duration-200 hover:bg-casino-gold/10 hover:text-casino-gold ${
                  lang.code === i18n.language
                    ? 'bg-casino-gold/20 text-casino-gold'
                    : 'text-gray-300'
                }`}
              >
                <span className="text-xl">{lang.flag}</span>
                <span>{lang.name}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
